import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin } from 'lucide-react';
import { CATEGORY_LANDINGS, GUIDES } from '../data/contentData';
import { getCategoryEditorial } from '../data/editorialContent';
import { SUPPLIERS, COUNTRIES, PRODUCTS } from '../data/mockData';
import SupplierCard from '../components/SupplierCard';
import EditorialBriefing from '../components/EditorialBriefing';
import SEO, { siteUrl } from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Img from '../components/Img';
import { track, EVENTS } from '../lib/analytics';

const CategoryLanding = () => {
  const { slug } = useParams();
  const config = CATEGORY_LANDINGS[slug];

  useEffect(() => {
    if (!config) return;
    track(EVENTS.CATEGORY_LANDING_VIEWED, { slug });
  }, [slug, !!config]);

  if (!config) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center">
        <div className="label-overline">404</div>
        <h1 className="mt-4 font-display text-4xl tracking-tighter">Category not found</h1>
        <Link to="/factories" className="mt-6 inline-block btn-giwahs btn-giwahs-primary">Back to directory</Link>
      </div>
    );
  }

  const matchingSuppliers = SUPPLIERS.filter(s =>
    config.productCategorySlugs.some(cat => s.categories.includes(cat))
  );
  const featuredSuppliers = matchingSuppliers.filter(s => s.featured).slice(0, 2);
  const topByTrust = [...matchingSuppliers].sort((a, b) => b.trustScore - a.trustScore).slice(0, 6);
  const relatedProducts = PRODUCTS.filter(p => config.productCategorySlugs.includes(p.category)).slice(0, 4);
  const relatedGuides = GUIDES.filter(g => config.relatedGuides.includes(g.slug));
  const editorial = getCategoryEditorial(slug);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Factories', url: '/factories' },
    { name: config.h1, url: `/category/${config.slug}` },
  ];

  return (
    <div data-testid="category-landing-page" className="surface-obsidian text-platinum">
      <SEO
        title={config.title.replace(' — GIWAHS', '')}
        description={editorial?.intro?.[0] || config.intro}
        breadcrumbs={breadcrumbItems}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: config.h1,
          description: config.intro,
          url: `${siteUrl}/category/${config.slug}`,
          isPartOf: { '@type': 'WebSite', name: 'GIWAHS', url: siteUrl },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: matchingSuppliers.length,
            itemListElement: matchingSuppliers.slice(0, 10).map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: { '@type': 'Organization', name: s.name, url: `${siteUrl}/supplier/${s.slug}` },
            })),
          },
        }}
      />
      {/* Breadcrumbs */}
      <div className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      {/* HERO */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <Link to="/factories" className="inline-flex items-center gap-2 label-overline text-neutral-500 hover:text-black">
            <ArrowLeft size={14} strokeWidth={1.5} /> Factory directory
          </Link>
          <span className="mt-8 inline-block label-overline">/ Category landing</span>
          <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6.5rem)', lineHeight: 0.92 }}>
            {config.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">{config.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/rfq" className="btn-giwahs btn-giwahs-primary">Request Quote</Link>
            <Link to="/factories" className="btn-giwahs btn-giwahs-secondary">Browse full directory</Link>
          </div>
        </div>
      </section>

      {/* TRUST HIGHLIGHTS */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-2 gap-px bg-black md:grid-cols-4">
            {[
              ['Verified Factories', String(matchingSuppliers.length)],
              ['Avg Trust Score', String(matchingSuppliers.length ? Math.round(matchingSuppliers.reduce((a, s) => a + s.trustScore, 0) / matchingSuppliers.length) : 0)],
              ['Countries Covered', String(new Set(matchingSuppliers.map(s => s.country)).size)],
              ['Lowest MOQ', String(matchingSuppliers.length ? Math.min(...matchingSuppliers.map(s => s.moqMin)) : 0)],
            ].map(([l, v]) => (
              <div key={l} className="bg-white p-5">
                <div className="label-overline text-neutral-500">{l}</div>
                <div className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL BRIEFING */}
      <EditorialBriefing
        briefing={editorial}
        kicker={`/ Editorial briefing · ${config.h1}`}
        title="Sourcing intelligence at a glance."
        subjectName={config.h1}
        relatedGuides={relatedGuides}
      />

      {/* COUNTRY FILTER SHORTCUTS */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <span className="label-overline">/ Filter by sourcing country</span>
          <div className="mt-5 flex flex-wrap gap-2">
            {COUNTRIES.map(c => {
              const matches = matchingSuppliers.filter(s => s.country === c).length;
              if (!matches) return null;
              return (
                <Link
                  key={c}
                  to={`/factories?q=${encodeURIComponent(c)}`}
                  data-testid={`cat-country-${c.toLowerCase()}`}
                  className="giwahs-tab-gold gap-2"
                >
                  <MapPin size={12} strokeWidth={1.5} />
                  <span className="label-overline">{c}</span>
                  <span className="font-mono-data text-[10px] text-neutral-500">{matches}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED SUPPLIERS (sponsored) */}
      {featuredSuppliers.length > 0 && (
        <section className="border-b border-hairline surface-obsidian">
          <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
            <div className="flex items-end justify-between border-b border-black pb-4">
              <div>
                <span className="label-overline">/ Featured Suppliers · Sponsored</span>
                <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>Featured for this category.</h2>
              </div>
              <span className="font-mono-data text-[11px] text-neutral-500">PAID PROMOTIONAL · TRUST INDEPENDENT</span>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {featuredSuppliers.map((s, i) => <SupplierCard key={s.id} supplier={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* TOP BY TRUST */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline">/ Top factories by Trust Score</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>The shortlist.</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {topByTrust.map((s, i) => <SupplierCard key={s.id} supplier={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* RELATED PRODUCTS */}
      {relatedProducts.length > 0 && (
        <section className="border-b border-hairline surface-obsidian-2">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
            <span className="label-overline">/ Related products</span>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 md:grid-cols-4">
              {relatedProducts.map(p => (
                <Link key={p.id} to={`/product/${p.slug}`} className="group flex flex-col border border-black bg-white">
                  <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
                    <Img src={p.image} alt={`${p.name} — product reference`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="font-display text-base" style={{ fontWeight: 600 }}>{p.name}</div>
                    <div className="mt-2 font-mono-data text-xs text-neutral-500">MOQ {p.moq} · {p.pricing}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GUIDES */}
      {relatedGuides.length > 0 && (
        <section className="border-b border-hairline surface-obsidian">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
            <span className="label-overline">/ Sourcing guides</span>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {relatedGuides.map(g => (
                <Link key={g.slug} to={`/sourcing-guides/${g.slug}`} className="group border border-black bg-white">
                  <div className="aspect-[16/10] overflow-hidden"><Img src={g.cover} alt={`${g.title} — related sourcing guide`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <div className="p-5">
                    <span className="label-overline">{g.categoryLabel}</span>
                    <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{g.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid items-end gap-10 md:grid-cols-2">
            <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              Sourcing {config.h1.toLowerCase()}? Submit an RFQ.
            </h2>
            <div className="flex md:justify-end">
              <Link to="/rfq" data-testid="cat-final-rfq" className="btn-giwahs btn-giwahs-dark inline-flex items-center gap-2">
                Build my RFQ <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryLanding;
